import { SearchRounded } from '@mui/icons-material';
import { Box, Button as MuiButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { PinCandidate } from '../../perps-v1-shared/types';

function CandidateRow({
  candidate,
  onAdd,
}: {
  candidate: PinCandidate;
  onAdd: (symbol: string) => void;
}) {
  const translate = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {candidate.displaySymbol}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {candidate.dex ? `${candidate.symbol} · ${candidate.dex}` : candidate.symbol}
        </Typography>
      </Box>
      <MuiButton size="small" variant="outlined" color="success" onClick={() => onAdd(candidate.symbol)}>
        {translate('module.perpsV1Views.membersAdd')}
      </MuiButton>
    </Stack>
  );
}

interface Props {
  // Already filtered by the dialog to: candidates MINUS matchedSymbols MINUS includeSymbols MINUS
  // excludeSymbols — see decision D13 ("one symbol, one place"). This component only adds the
  // client-side search over symbol + displaySymbol on top.
  candidates: PinCandidate[];
  loading: boolean;
  onAdd: (symbol: string) => void;
}

// Left column of the INCLUDE editor — the only remaining "picker" half now that the right side
// IS the live preview/member list (see ViewPreviewPanel). A symbol leaves this list the instant
// it's added, matched by the rule, pinned, or excluded — there is no "add this twice" state to
// guard against, and it never shows a symbol that's already in the view by some other means.
export default function ViewAvailableSymbols({ candidates, loading, onAdd }: Props) {
  const translate = useTranslate();
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return candidates;
    return candidates.filter(
      (c) => c.symbol.toLowerCase().includes(key) || c.displaySymbol.toLowerCase().includes(key)
    );
  }, [candidates, keyword]);

  // Two distinct empty states: a keyword that matched nothing (membersNoResult, existing) vs. the
  // pool itself being empty — which for a rule that already matches every candidate (e.g.
  // "Gainers", 278 of 278) is the NORMAL case, not a broken picker, so it gets its own plain-
  // language explanation pointing at Pins for ranking instead.
  let emptyMessageKey: string | null = null;
  if (!loading) {
    if (candidates.length === 0) {
      emptyMessageKey = 'module.perpsV1Views.membersAvailableEmpty';
    } else if (filtered.length === 0) {
      emptyMessageKey = 'module.perpsV1Views.membersNoResult';
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('module.perpsV1Views.membersAvailable', { count: candidates.length })}
      </Typography>
      <TextField
        fullWidth
        size="small"
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder={translate('module.perpsV1Views.membersSearch')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      <Box sx={{ height: 320, position: 'relative' }}>
        <Stack spacing={0} sx={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {translate('module.perpsV1Views.membersLoading')}
            </Typography>
          ) : emptyMessageKey ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {translate(emptyMessageKey)}
            </Typography>
          ) : (
            filtered.map((candidate) => (
              <CandidateRow key={candidate.symbol} candidate={candidate} onAdd={onAdd} />
            ))
          )}
        </Stack>
      </Box>
    </Paper>
  );
}
